import React, { useState, useEffect } from 'react';
import { Plus, Play, MoreVertical, Lock, X, Music, PencilIcon, ShareIcon, TrashIcon, FileText, Mail, Trash, Search, Users, Check, Video } from 'lucide-react';
import Sidebar from '../../Sidebar/Sidebar';
import Navbar from '../../Navbars/UserNavbar';
import { DashboardApi } from '../../../apis/DashboardApi';
import { AxiosResponse } from 'axios';
import { IoInformation } from 'react-icons/io5';
import { NavLink } from 'react-router-dom';
import { convertUTCDateToLocalDate } from '../../../functions';
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useSelector, useDispatch } from 'react-redux';
import MediaGridWithPagination from './MediaGridWithPagination';

interface Tab {
    id: string;
    label: string;
}

interface Media {
    id: number;
    file_name: string;
    file_type: string;
    temporaryUrl: string;
    is_legacy: number;
    created_at?: string;
    file_size?: string;
    recipients?: any;
    recipientTemporaryUrl?: any;
    handleDelete?: () => void;
    handleRename?: () => void;
    handleShare?: () => void;
}

interface MediaResponse {
    status: string;
    data: {
        files: {
            data: Media[];
        };
        collections: any[];
    };
}

const formatSize = (bytes: any): any => {
    if (bytes < 1024) return `${bytes}MB`; // If less than 1GB, show in MB
    else if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)}GB`; // If less than 1TB, show in GB
    else return `${(bytes / (1024 * 1024)).toFixed(2)}TB`; // Otherwise, show in TB
};

interface MediaModalProps {
    media: Media | null;
    onClose: () => void;
}

const MediaModal: React.FC<MediaModalProps> = ({ media, onClose }) => {
    if (!media) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="relative bg-white rounded-lg max-w-xl w-full mx-4" onClick={e => e.stopPropagation()}>
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 z-10"
                >
                    <X className="w-6 h-6" />
                </button>

                <div className="p-4">
                    {media.file_type === 'video' && (
                        <div className="aspect-video p-2 pt-5">
                            <video
                                src={media.temporaryUrl}
                                controls
                                className="w-full h-full rounded-lg"
                            >
                                Your browser does not support video playback.
                            </video>
                        </div>
                    )}

                    {media.file_type === 'image' && (
                        <div className="relative max-h-[80vh] overflow-hidden p-2 pt-5">
                            <img
                                src={media.temporaryUrl}
                                alt={media.file_name}
                                className="w-full h-[400px] object-contain rounded-lg"
                            />
                        </div>
                    )}

                    {media.file_type === 'audio' && (
                        <div className="p-2 pt-5">
                            <audio
                                src={media.temporaryUrl}
                                controls
                                className="w-full"
                            >
                                Your browser does not support audio playback.
                            </audio>
                        </div>
                    )}

                    {media.file_type === 'document' && (
                        <div className="p-2 pt-5">
                            <iframe
                                src={`https://docs.google.com/viewer?url=${encodeURIComponent(media.temporaryUrl)}&embedded=true`}
                                className="w-full max-h-[600px] border rounded-md"
                                frameBorder="0"
                            >
                                Your browser does not support viewing documents.
                            </iframe>
                        </div>
                    )}

                    {/* {media.file_type === 'document' && (
                        <div className="p-8 ">
                            <iframe
                                src={media.temporaryUrl}
                                className="w-full h-[100px] border rounded-md"
                            // frameBorder="0"
                            >
                                Your browser does not support viewing documents.
                            </iframe>
                        </div>
                    )} */}


                    <div className="mt-4 flex justify-between ">
                        <div className="text-[12px] leading-7 font-medium">
                            <h3 >File Name: {media.file_name}</h3>
                            <h3 >File Size: {formatSize(media.file_size)}</h3>
                            <h3 >Date Created: {convertUTCDateToLocalDate(media?.created_at)}</h3>
                        </div>
                    </div>

                    <div className='mt-3'>
                        <h3 className='text-[#767676] text-[14px]'>Connected to:</h3>
                        <span className='flex flex-wrap gap-2 max-h-10 overflow-auto'>
                            {media?.recipients?.map((data: any) => (
                                <div className="flex items-center  gap-1">
                                    <img
                                        src={data?.temporaryUrl || 'images/profile_image.jpg'}
                                        alt={`${data?.first_name} ${data?.last_name}`}
                                        className="w-10 h-10 rounded-full object-cover mt-1 "
                                    />
                                    <h3 className="font-medium text-[12px]">{`${data?.first_name} ${data?.last_name}`}</h3>
                                </div>
                            ))}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};


interface ShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    mediaId: number;
    mediaName: string;
}

const ShareModal = ({ isOpen, onClose, mediaId, mediaName }: ShareModalProps) => {
    const [email, setEmail] = useState('');
    const [sharedWith, setSharedWith] = useState<Array<{ id: number, email: string }>>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            fetchSharedUsers();
        }
    }, [isOpen, mediaId]);

    const fetchSharedUsers = async () => {
        try {
            const response: any = await DashboardApi.getSharedUsers(mediaId);
            if (response.data?.data) {
                setSharedWith(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching shared users:', error);
        }
    };

    const handleShare = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response: any = await DashboardApi.shareMedia({
                media_id: mediaId,
                email: email
            });

            if (response.data?.status) {
                toast.success('Shared successfully');
                setEmail('');
                fetchSharedUsers(); // Refresh the list
            } else {
                setError(response.data?.message || 'Failed to share');
            }
        } catch (error: any) {
            setError(error.response?.data?.message || 'Failed to share');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveAccess = async (sharedId: number) => {
        try {
            const response: any = await DashboardApi.removeSharedAccess(sharedId);
            if (response.data?.status) {
                toast.success('Access removed successfully');
                fetchSharedUsers(); // Refresh the list
            }
        } catch (error) {
            toast.error('Failed to remove access');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">Share "{mediaName}"</h2>
                    <button onClick={() => onClose()} className="text-gray-500 hover:text-gray-700">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleShare} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Share with email</label>
                        <div className="mt-1 flex gap-2">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter email address"
                                className="flex-1 rounded-lg border p-2"
                                required
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                className="bg-blue-500 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                            >
                                {loading ? 'Sharing...' : 'Share'}
                            </button>
                        </div>
                        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                    </div>
                </form>

                <div className="mt-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Shared with</h3>
                    <div className="space-y-2">
                        {sharedWith.map((user) => (
                            <div key={user.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                <div className="flex items-center gap-2">
                                    <Mail className="w-4 h-4 text-gray-500" />
                                    <span>{user.email}</span>
                                </div>
                                <button
                                    onClick={() => handleRemoveAccess(user.id)}
                                    className="text-red-500 hover:text-red-700"
                                >
                                    <Trash className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                        {sharedWith.length === 0 && (
                            <p className="text-gray-500 text-sm">Not shared with anyone yet</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};



interface GroupModalProps {
    isOpen: boolean;
    onClose: () => void;
    media: Media[];
}

interface GroupMember {
    id: number;
    email: string;
}

interface GroupMedia {
    id: number;
    file_name: string;
    file_type: string;
    temporaryUrl: string;
}

const GroupSharingModal: React.FC<GroupModalProps> = ({ isOpen, onClose, media }) => {
    const [groupName, setGroupName] = useState('');
    const [selectedMedia, setSelectedMedia] = useState<GroupMedia[]>([]);
    const [members, setMembers] = useState<GroupMember[]>([]);
    const [newEmail, setNewEmail] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleAddMember = (e: React.FormEvent) => {
        e.preventDefault();
        if (newEmail && !members.find(m => m.email === newEmail)) {
            setMembers([...members, { id: Date.now(), email: newEmail }]);
            setNewEmail('');
        }
    };

    const handleRemoveMember = (email: string) => {
        setMembers(members.filter(m => m.email !== email));
    };

    const handleToggleMedia = (mediaItem: GroupMedia) => {
        const isSelected = selectedMedia.find(m => m.id === mediaItem.id);
        if (isSelected) {
            setSelectedMedia(selectedMedia.filter(m => m.id !== mediaItem.id));
        } else {
            setSelectedMedia([...selectedMedia, mediaItem]);
        }
    };

    const handleCreateGroup = async () => {
        if (!groupName) {
            setError('Group name is required');
            return;
        }
        if (members.length === 0) {
            setError('Add at least one member');
            return;
        }
        if (selectedMedia.length === 0) {
            setError('Select at least one media file');
            return;
        }

        setLoading(true);
        try {
            const response: any = await DashboardApi.createGroup({
                name: groupName,
                emails: members.map(m => m.email),
                media_ids: selectedMedia.map(m => m.id)
            });

            if (response.data?.status) {
                toast.success('Group created successfully');
                onClose();
            }
        } catch (error: any) {
            setError(error.response?.data?.message || 'Failed to create group');
        } finally {
            setLoading(false);
        }
    };

    const filteredMedia = media.filter(m =>
        m.file_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold">Create Sharing Group</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="space-y-6">
                    {/* Group Name Input */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Group Name
                        </label>
                        <input
                            type="text"
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            className="w-full p-2 border rounded-lg"
                            placeholder="Enter group name"
                        />
                    </div>

                    {/* Members Section */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Add Members
                        </label>
                        <form onSubmit={handleAddMember} className="flex gap-2 mb-4">
                            <input
                                type="email"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                className="flex-1 p-2 border rounded-lg"
                                placeholder="Enter email address"
                            />
                            <button
                                type="submit"
                                className="bg-blue-500 text-white px-4 py-2 rounded-lg"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </form>

                        <div className="space-y-2">
                            {members.map((member) => (
                                <div key={member.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                    <div className="flex items-center gap-2">
                                        <Mail className="w-4 h-4 text-gray-500" />
                                        <span>{member.email}</span>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveMember(member.email)}
                                        className="text-red-500 hover:text-red-700"
                                    >
                                        <Trash className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Media Selection Section */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Media
                        </label>
                        <div className="flex gap-2 mb-4">
                            <Search className="w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="flex-1 p-2 border rounded-lg"
                                placeholder="Search media files"
                            />
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {filteredMedia.map((item) => (
                                <div
                                    key={item.id}
                                    onClick={() => handleToggleMedia(item)}
                                    className={`relative rounded-lg overflow-hidden cursor-pointer border-2 
            ${selectedMedia.find(m => m.id === item.id)
                                            ? 'border-blue-500'
                                            : 'border-transparent'}`}
                                >
                                    {/* Image Preview */}
                                    {item.file_type === 'image' && (
                                        <img
                                            src={item.temporaryUrl}
                                            alt={item.file_name}
                                            className="w-full h-32 object-cover"
                                        />
                                    )}

                                    {/* Video Preview */}
                                    {item.file_type === 'video' && (
                                        <div className="relative w-full h-32 bg-gray-100">
                                            <video
                                                src={item.temporaryUrl}
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <Video className="w-8 h-8 text-gray-500" />
                                            </div>
                                        </div>
                                    )}

                                    {/* Audio Preview */}
                                    {item.file_type === 'audio' && (
                                        <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
                                            <Music className="w-8 h-8 text-gray-500" />
                                        </div>
                                    )}

                                    {/* Document Preview */}
                                    {item.file_type === 'document' && (
                                        <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
                                            <FileText className="w-8 h-8 text-gray-500" />
                                        </div>
                                    )}

                                    {/* File Info Bar */}
                                    <div className="p-2 bg-gray-50">
                                        <p className="text-sm truncate">{item.file_name}</p>
                                        <p className="text-xs text-gray-500">
                                            {item.file_type.charAt(0).toUpperCase() + item.file_type.slice(1)}
                                        </p>
                                    </div>

                                    {/* Selection Indicator */}
                                    {selectedMedia.find(m => m.id === item.id) && (
                                        <div className="absolute top-2 right-2">
                                            <div className="bg-blue-500 rounded-full p-1">
                                                <Check className="w-4 h-4 text-white" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {error && (
                        <p className="text-red-500 text-sm">{error}</p>
                    )}

                    <div className="flex justify-end gap-4">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCreateGroup}
                            disabled={loading}
                            className="px-6 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
                        >
                            {loading ? 'Creating...' : 'Create Group'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


const DeleteMediaModal: React.FC<MediaModalProps> = ({ media, onClose }) => {
    if (!media) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="relative bg-white rounded-lg max-w-4xl w-full mx-4" onClick={e => e.stopPropagation()}>
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 z-10"
                >
                    <X className="w-6 h-6" />
                </button>

                <div className="p-4">
                    <p>Are you sure you want to delete this media?</p>

                    <button
                        onClick={() => onClose}
                        className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 z-10"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};
interface DropdownPosition {
    x: number;
    y: number;
}
interface MediaDropdownProps {
    isOpen: boolean;
    onClose: (e: any) => void;
    onDelete: (e: any) => void;
    position: DropdownPosition;
    isShareModalOpen: any;
    toggleShareMediaOpen: () => void;
    isShareModalClose: () => void;
    mediaId: any;
    mediaName: any;
}


const MediaDropdown: React.FC<MediaDropdownProps> = ({ isOpen, onClose, position, onDelete, isShareModalOpen, isShareModalClose, mediaId, mediaName, toggleShareMediaOpen }) => (
    <div
        className={` z-0 bg-white rounded-lg shadow-lg py-1 ${isOpen ? 'block' : 'hidden'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
            top: "100%",  // Position below the button
            right: 0,     // Align to right
            minWidth: "160px"
        }}
    >
        <div className='flex justify-end'><X className='text-orange-500' onClick={(e) => {
            e.stopPropagation();
            onClose(e);
        }} /></div>
        {/* <button className={`w-full px-4 py-2.5 text-sm text-left transition-colors flex items-center gap-2 hover:bg-orange-50 hover:text-orange-500 text-orange-500`}>
            <PencilIcon className="w-4 h-4" />
            Rename
        </button> */}
        {/* <button onClick={toggleShareMediaOpen} className={`w-full px-4 py-2.5 text-sm text-left transition-colors flex items-center gap-2 hover:bg-orange-50 hover:text-orange-500 text-orange-500`}>
            <ShareIcon className="w-4 h-4" />
            Share

            <ShareModal
                isOpen={isShareModalOpen}
                onClose={isShareModalClose}
                mediaId={mediaId}
                mediaName={mediaName}
            />
        </button> */}
        {/* <button className={`w-full px-4 py-2.5 text-sm text-left transition-colors flex items-center gap-2 hover:bg-orange-50 hover:text-orange-500 text-orange-500`}>
            <IoInformation className="w-4 h-4" />
            Media Details
        </button> */}
        <hr className="my-1 border-gray-100" />
        <button onClick={onDelete} className={`w-full px-4 py-2.5 text-sm text-left transition-colors flex items-center gap-2 hover:bg-red-50 hover:text-red-600 text-red-500`}>
            <TrashIcon className="w-4 h-4" />
            Delete
        </button>
    </div>
);

const Media = () => {
    const [media, setMedia] = useState<Media[]>([]);

    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('my-media');
    const [activeFilter, setActiveFilter] = useState('all');
    const userLoginData = useSelector((state: any) => state.data.login.value);
    const sharedData = useSelector((state: any) => state.data.shared.value);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);

    // console?.log(currentPage)

    const [mediaSize, setMediaSize] = useState<any>(sharedData?.data?.total_used_mb);
    // console?.log(sharedData?.data?.total_used_mb)

    const mainTabs: Tab[] = [
        { id: 'my-media', label: 'My Media' },
        // { id: 'schedule-media', label: 'Schedule Media' },
        // { id: 'legacy', label: 'Legacy' }
    ];

    const filters: Tab[] = [
        { id: 'all', label: 'All' },
        { id: 'video', label: 'Video' },
        { id: 'image', label: 'Image' },
        { id: 'audio', label: 'Audio' },
        { id: 'document', label: 'Document' }
    ];

    //   useEffect(() => {
    //     fetchMedia();
    //   }, []);

    const fetchMedia = (page: number = 1): void => {
        console?.log(page)
        setLoading(true);
        DashboardApi.getMedia(page)
            .then((response: AxiosResponse<any>) => {
                if (response?.data) {
                    setMedia(response?.data?.data?.files?.data);
                    // Update pagination info from the API response
                    setCurrentPage(response?.data?.data?.files?.current_page);
                    setTotalPages(response?.data?.data?.files?.last_page);
                    setLoading(false);
                }
            })
            .catch(function (error) {
                console.error('Error fetching media:', error);
                setLoading(false);
            });
    };


    React.useEffect(() => {

        DashboardApi.myGroups().then(
            (response: AxiosResponse<any>) => {
                if (response?.data) {
                    // console?.log(response?.data)
                }
            }
        )

        // DashboardApi.getMedia().then(
        //     (response: AxiosResponse<any>) => {
        //         if (response?.data) {
        //             setMedia(response?.data?.data?.files?.data)
        //             setLoading(false)
        //         }
        //     }
        // ).catch(function (error) {
        //     // handle error

        // })

        fetchMedia(1);


    }, []);

    const [selectedMedia, setSelectedMedia] = useState<any | null>(null);

    const handleMediaClick = (media: any) => {
        setSelectedMedia(media);

    };
    // console?.log(selectedMedia)

    const deleteMedia = () => {
        setLoading(true)
        DashboardApi.deleteMedia(dropdownOpen).then(
            (response: AxiosResponse<any>) => {
                if (response?.data) {
                    DashboardApi.getMedia(currentPage).then(
                        (response: AxiosResponse<any>) => {
                            if (response?.data) {
                                setMedia(response?.data?.data?.files?.data)
                                toast.success("Media deleted successfully")
                                setLoading(false)
                                setDeleteModal(false)
                            }
                        }
                    )
                }
            }
        ).catch(function (error) {
            // handle error

        })

    };




    // Add state in parent component:
    const [dropdownOpen, setDropdownOpen] = useState<number | null | Media>(null);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    // Add click outside handler:
    // useEffect(() => {
    //     if (dropdownOpen !== null) {
    //         const handleClickOutside = () => setDropdownOpen(null);
    //         document.addEventListener('click', handleClickOutside);
    //         return () => document.removeEventListener('click', handleClickOutside);
    //     }
    // }, []);



    const getMediaCard = (item: Media) => {
        const commonClasses = "relative rounded-lg overflow-hidden bg-gray-100 h-[300px] w-full";
        const bottomBarClasses = "absolute bottom-0 left-0 right-0 flex justify-between items-center p-3 bg-gradient-to-t from-black/50 to-transparent";
        const privateTagClasses = "absolute top-2 right-2 flex items-center gap-2 bg-black/30 px-2 py-1 rounded";

        switch (item.file_type) {
            case 'video':
                return (
                    <div className={commonClasses}>
                        {/* <video
                                src={media.temporaryUrl}
                                controls
                                className="w-full h-full rounded-lg"
                            >
                                Your browser does not support video playback.
                            </video> */}

                        <video src={item.temporaryUrl} className="w-full h-full object-cover" />
                        <div onClick={() => handleMediaClick(item)} className="absolute inset-0 flex items-center justify-center">
                            <button className="bg-black/50 rounded-full p-3 hover:bg-black/70">
                                <Play className="w-6 h-6 text-white" />
                            </button>
                        </div>
                        {item.is_legacy === 1 && (
                            <div className={privateTagClasses}>
                                <span className="text-white text-sm">Legacy</span>
                            </div>
                        )}
                        <div className={bottomBarClasses}>
                            <span className="text-white text-sm truncate">{item.file_name}</span>
                            <button className="text-white" onClick={() => setDropdownOpen(item?.id)}>
                                <MoreVertical className="w-5 h-5" />
                                <MediaDropdown
                                    isOpen={dropdownOpen === item.id ? true : false}
                                    onClose={(e: any) => setDropdownOpen(null)}
                                    onDelete={(e: any) => setDeleteModal(true)}
                                    position={{ x: -120, y: 0 }}
                                    // onSahre = {}
                                    toggleShareMediaOpen={() => setIsShareModalOpen(true)}
                                    isShareModalOpen={isShareModalOpen}
                                    isShareModalClose={() => {
                                        setIsShareModalOpen(false);
                                        setSelectedMedia(null);
                                    }}
                                    mediaId={item?.id}
                                    mediaName={item.file_name}

                                />
                            </button>
                        </div>
                    </div>
                );

            case 'image':
                return (
                    <div className={commonClasses}>
                        <img onClick={() => handleMediaClick(item)} src={item.temporaryUrl} alt={item.file_name} className="w-full h-full object-cover" />
                        {item.is_legacy === 1 && (
                            <div className={privateTagClasses}>
                                <Lock className="w-4 h-4 text-white" />
                                <span className="text-white text-sm">Legacy</span>
                            </div>
                        )}
                        <div className={bottomBarClasses}>
                            <span className="text-white text-sm truncate">{item.file_name}</span>
                            <button className="text-white" onClick={() => setDropdownOpen(item?.id)}>
                                <MoreVertical className="w-5 h-5" />
                                <MediaDropdown
                                    isOpen={dropdownOpen === item.id ? true : false}
                                    onClose={(e: any) => setDropdownOpen(null)}
                                    onDelete={(e: any) => setDeleteModal(true)}
                                    position={{ x: -120, y: 0 }}
                                    toggleShareMediaOpen={() => setIsShareModalOpen(true)}
                                    isShareModalOpen={isShareModalOpen}
                                    isShareModalClose={() => {
                                        setIsShareModalOpen(false);
                                        setSelectedMedia(null);
                                    }}
                                    mediaId={item?.id}
                                    mediaName={item.file_name}
                                />
                            </button>
                        </div>
                    </div>
                );

            case 'audio':
                return (
                    <div className={`${commonClasses} bg-white border flex flex-col justify-center items-center`}>
                        <Music className="w-16 h-16 text-gray-400 mb-4" onClick={() => handleMediaClick(item)} />
                        <div className="w-3/4 h-2 bg-gray-200 rounded-full" />
                        {item.is_legacy === 1 && (
                            <div className={`${privateTagClasses} bg-gray-100`}>
                                <Lock className="w-4 h-4 text-gray-500" />
                                <span className="text-gray-400 text-sm">Legacy</span>
                            </div>
                        )}
                        <div className={bottomBarClasses}>
                            <span className="text-white text-sm truncate">{item.file_name}</span>
                            <button className="text-white" onClick={() => setDropdownOpen(item?.id)}>
                                <MoreVertical className="w-5 h-5 text-white" />
                                <MediaDropdown
                                    isOpen={dropdownOpen === item.id}
                                    onClose={() => setDropdownOpen(null)}
                                    position={{ x: -120, y: 0 }}
                                    onDelete={(e: any) => setDeleteModal(true)}
                                    toggleShareMediaOpen={() => setIsShareModalOpen(true)}
                                    isShareModalOpen={isShareModalOpen}
                                    isShareModalClose={() => {
                                        setIsShareModalOpen(false);
                                        setSelectedMedia(null);
                                    }}
                                    mediaId={item?.id}
                                    mediaName={item.file_name}
                                />
                            </button>
                        </div>
                    </div>
                );

            case 'document':
                return (
                    <div

                        className={`${commonClasses} bg-white border flex flex-col justify-center items-center`}
                    >
                        <FileText onClick={() => handleMediaClick(item)} className="w-16 h-16 text-gray-400 mb-4" />
                        <span className="text-sm text-gray-500 truncate">{item.file_name}</span>
                        {item.is_legacy === 1 && (
                            <div className={`${privateTagClasses} bg-gray-100`}>
                                <Lock className="w-4 h-4 text-gray-500" />
                                <span className="text-gray-400 text-sm">Legacy</span>
                            </div>
                        )}
                        <div className={bottomBarClasses}>
                            <span className="text-white text-sm truncate">{item.file_name}</span>

                            <button className="text-white" onClick={() => setDropdownOpen(item?.id)}>
                                <MoreVertical className="w-5 h-5 text-white" />
                                <MediaDropdown
                                    isOpen={dropdownOpen === item.id}
                                    onClose={() => setDropdownOpen(null)}
                                    position={{ x: -120, y: 0 }}
                                    onDelete={(e: any) => setDeleteModal(true)}
                                    toggleShareMediaOpen={() => setIsShareModalOpen(true)}
                                    isShareModalOpen={isShareModalOpen}
                                    isShareModalClose={() => {
                                        setIsShareModalOpen(false);
                                        setSelectedMedia(null);
                                    }}
                                    mediaId={item?.id}
                                    mediaName={item.file_name}
                                />
                            </button>
                        </div>
                    </div>
                );


            default:
                return null;
        }
    };


    return (
        <div className="min-h-screen bg-white">
            <div className="flex flex-col md:flex-row">
                <Sidebar />
                <main className="flex-1 p-6 relative md:ml-64">
                    <Navbar />
                    <div className="p-4 md:p-6">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 space-y-4 md:space-y-0">
                            <div className="flex space-x-6 overflow-x-auto pb-2 md:pb-0">
                                {mainTabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        // onClick={() => handleMediaClick(tab)}
                                        className={`whitespace-nowrap pb-2 font-medium text-sm md:text-base border-b-2 cursor-pointer transition-colors
                      ${activeTab === tab.id
                                                ? 'text-orange-500 border-orange-500'
                                                : 'text-gray-600 border-transparent hover:text-orange-500'}`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            <div className="flex space-x-4">
                                <NavLink to={mediaSize < userLoginData?.data?.user_data?.max_upload_limit_mb ? '/add-media' : '/upgrade'}>
                                    <button className="flex items-center gap-2 px-4 py-2 text-orange-500 border border-orange-500 rounded-lg hover:bg-orange-50">
                                        <Plus className="w-5 h-5" />
                                        <span>{mediaSize < userLoginData?.data?.user_data?.max_upload_limit_mb ? 'Add media' : 'Upgrade'} </span>
                                    </button>
                                </NavLink>

                                <button
                                    onClick={() => setShowGroupModal(true)}
                                    className="flex items-center gap-2 px-4 py-2 text-blue-500 border border-blue-500 rounded-lg hover:bg-blue-50"
                                >
                                    <Users className="w-5 h-5" />
                                    <span>Create Group</span>
                                </button>
                            </div>

                        </div>

                        <div className="flex flex-wrap gap-2 mb-8">
                            {filters.map((filter) => (
                                <button
                                    key={filter.id}
                                    onClick={() => setActiveFilter(filter.id)}
                                    className={`px-6 py-2 rounded-full text-sm transition-colors
                    ${activeFilter === filter.id
                                            ? 'bg-orange-500 text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                >
                                    {filter.label}
                                </button>
                            ))}
                        </div>

                        {/* <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"> */}
                        
                       
                                <MediaGridWithPagination
                                    media={media}
                                    loading={loading}
                                    activeFilter={activeFilter}
                                    getMediaCard={getMediaCard}
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={(page) => fetchMedia(page)}
                                    isServerPagination={true}
                                />
                            {/* </div> */}
                    </div>
                </main>

                {selectedMedia && (
                    <div className='t-20'>
                        <MediaModal
                            media={selectedMedia}
                            onClose={() => setSelectedMedia(null)}
                        />
                    </div>
                )}
            </div>

            <Modal open={deleteModal} onClose={() => setDeleteModal(false)} center>
                <div className="md:w-[500px]  body-font font-poppins flex justify-center">
                    <div className="p-4 justify-center ">
                        <p>Are you sure you want to delete this media?</p>
                        <div className='flex justify-center mt-3'>
                            <button disabled={loading} onClick={() => deleteMedia()} type="button" className="px-6 py-2  bg-orange-500 text-white rounded-lg">
                                {loading ? 'Please wait...' : 'Delete'}
                            </button>
                        </div>

                    </div>

                </div>
            </Modal>

            <GroupSharingModal
                isOpen={showGroupModal}
                onClose={() => setShowGroupModal(false)}
                media={media}
            />


            <ToastContainer
                position="top-right"
                autoClose={2000}
                hideProgressBar={true}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover />
        </div>
    );
};

export default Media;